import {RecoilState, RecoilValueReadOnly, SerializableParam, useRecoilValueLoadable, useSetRecoilState} from 'recoil';
import {useMemo, useRef} from 'react';

interface QueryValue<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  refresh: VoidFunction;
}

type QuerySelector<T, R = T> = (query: T) => R;
type Config<S, R> = {
  selector?: QuerySelector<QueryValue<S>, R>;
  withCache: boolean;
};

export function createQuery<S, P extends SerializableParam>(
  query: (params: P) => RecoilValueReadOnly<S>,
  refreshProp: RecoilState<number>,
) {
  return <R = QueryValue<S>>(params: P, config?: Config<S, R>): R | QueryValue<S> => {
    const {selector, withCache = true} = config || {};

    const cachedData = useRef<S | null>(null);
    const loadable = useRecoilValueLoadable(query(params));
    const refreshFn = useSetRecoilState(refreshProp);

    const refresh = () => refreshFn((prev) => prev + 1);

    const data = useMemo(() => {
      if (!withCache) return loadable.contents;

      if (loadable.state === 'hasValue') {
        cachedData.current = loadable.contents;
      }
      return cachedData.current;
    }, [loadable.state, loadable.contents, withCache]);

    const result: QueryValue<S> = {
      data,
      error: loadable.state === 'hasError' ? loadable.contents : null,
      loading: loadable.state === 'loading',
      refresh,
    };

    return selector ? selector(result) : result;
  };
}
